import { Divider, Group, Modal, Stack, Text } from '@mantine/core';
import { BillingDetail } from './hooks/useBilling';
import { formatPenceToPounds, getBillingTypeLabel } from './billingUtils';
import { StatRow } from './StatRow';
import { TotalRow } from './TotalRow';
import { BillSummaryRow } from './BillSummaryRow';

type BillingDetailModalProps = {
  billingDetail: BillingDetail | null;
  onClose: () => void;
};

/** The firm's own headcount against what the platform actually bills for. */
function statedComparison(detail: BillingDetail): string {
  if (detail.statedBillableUnits === null) return 'Not stated';

  const difference = detail.billableUnitCount - detail.statedBillableUnits;
  if (difference === 0) return 'Matches stated families';

  return difference > 0
    ? `${difference.toLocaleString('en-GB')} more than stated`
    : `${Math.abs(difference).toLocaleString('en-GB')} fewer than stated`;
}

export default function BillingDetailModal({ billingDetail, onClose }: BillingDetailModalProps) {
  if (!billingDetail) return null;

  const isFixed = billingDetail.billingType === 'FIXED';

  return (
    <Modal opened={!!billingDetail} onClose={onClose} title={billingDetail.companyName} size={1100}>
      <Group align="flex-start" gap="xl" wrap="nowrap">
        <div style={{ flex: 7 }}>
          <Stack gap="sm">
            <Text fw={600} size="sm">
              Client & family stats
            </Text>

            <StatRow
              label="All clients"
              value={billingDetail.billableClientsCount}
              hint="allClients"
            />
            <StatRow label="Clients without family" value={billingDetail.clientsWithoutFamily} />
            <StatRow label="Families" value={billingDetail.familiesCount} />
            <TotalRow label="All units" value={billingDetail.billableUnitCount} hint="allUnits" />
            <StatRow
              label="Families stated by the firm"
              value={billingDetail.statedBillableUnits ?? '-'}
            />
            <StatRow label="All units vs stated" value={statedComparison(billingDetail)} />

            <Divider />

            <Text fw={600} size="sm">
              Activity
            </Text>

            <StatRow
              label="Active clients"
              value={billingDetail.activeClientsCount}
              hint="activeClients"
            />
            <StatRow
              label="Active clients without family"
              value={billingDetail.activeClientsWithoutFamily}
            />
            <StatRow label="Active families" value={billingDetail.activeFamiliesCount} />
            <TotalRow
              label="Billed units"
              value={billingDetail.activeBillableUnitCount}
              hint="billedUnits"
            />

            <Divider />

            <Text fw={600} size="sm">
              Advised split (not billed on)
            </Text>

            <StatRow label="Advised clients" value={billingDetail.advisedClientsCount} />
            <StatRow label="Non-advised clients" value={billingDetail.nonAdvisedClientsCount} />
            <StatRow label="Advised families" value={billingDetail.advisedFamiliesCount} />
            <StatRow
              label="Non-advised-only families"
              value={billingDetail.nonAdvisedOnlyFamiliesCount}
            />
          </Stack>
        </div>
        <Divider orientation="vertical" />
        <div style={{ flex: 5 }}>
          <Stack gap="sm">
            <Text fw={600} size="sm">
              Billing
            </Text>

            <StatRow label="Type" value={getBillingTypeLabel(billingDetail.billingType)} />
            <StatRow label="Start date" value={formatStartDate(billingDetail.billingStartDate)} />
            {isFixed ? (
              <TotalRow label="Total amount" value={formatAmount(billingDetail.billingAmount)} />
            ) : (
              <StatRow
                label="Billing amount"
                value={formatAmount(billingDetail.billingAmount)}
                hint="billingAmount"
              />
            )}
            {billingDetail.minimumBillingAmount != null && (
              <StatRow
                label="Minimum amount"
                value={formatAmount(billingDetail.minimumBillingAmount)}
              />
            )}
            {!isFixed && (
              <BillSummaryRow
                label="Monthly bill"
                units={billingDetail.activeBillableUnitCount}
                bill={formatPenceToPounds(billingDetail.monthlyBillPence)}
                calculatedBill={calculateBillBeforeMinimum(
                  billingDetail.billingAmount,
                  billingDetail.activeBillableUnitCount,
                )}
                isMinApplied={isMinApplied(
                  billingDetail.monthlyBillPence,
                  billingDetail.minimumBillingAmount,
                )}
              />
            )}
          </Stack>
        </div>
      </Group>
    </Modal>
  );
}

function calculateBillBeforeMinimum(
  billingAmount: number | null | undefined,
  units: number,
): string | null {
  if (billingAmount == null) return null;
  const amount = Number(billingAmount);
  if (Number.isNaN(amount)) return null;
  const pence = Math.floor(amount * units * 100);
  return formatPenceToPounds(pence);
}

function isMinApplied(billPence: number | null, minAmount: number | null | undefined): boolean {
  if (billPence == null || minAmount == null) return false;
  return billPence === Math.floor(Number(minAmount) * 100);
}

function formatStartDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

function formatAmount(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '-';
  return `£${Number(amount).toFixed(2)}`;
}
